import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import RuleDiffAttributeCard from "@/components/RuleDiffAttributeCard";
import { ApiError } from "@/lib/api";
import type { UserProfile } from "@/lib/profile-api";
import { isoToBrowseCode } from "@/lib/rulediff-browse-countries";
import { getBrowseState } from "@/lib/rulediff-browse-states";
import {
  ATTR_DISPLAY_ORDER,
  parseSeverity,
  type RuleDiffSeedEntry,
  type RuleDiffSeverity,
  getSeedRuleDiff,
} from "@/lib/rulediff-seed-api";
import type { UsStateCode } from "@/lib/states";

/**
 * apps/web :: components/dashboard :: RuleDiffBrowsePanel
 *
 * Compact dashboard surface for the precomputed structured-browse
 * corpus (Wave 17d /rule-diff/browse). Sits next to the existing
 * RuleDiffPanel (LLM-backed prose diff). Where RuleDiffPanel is
 * "personalized prose synthesis", this panel is "deterministic
 * structured digest of the safety-critical rule diffs that apply
 * RIGHT NOW based on your profile + current state".
 *
 * UX:
 *   - Resolves (country, state) from profile + activeState
 *   - Fetches the 10-attribute corpus row via getSeedRuleDiff
 *   - Renders the TOP 3 attributes by severity (critical first, then
 *     warning, then canonical order) — keeps the panel compact while
 *     surfacing the most safety-critical bilateral diffs
 *   - "See all 10" CTA → /rule-diff/browse (full surface with picker
 *     for switching country/state)
 *   - 404 from API → empty state (current pair not in corpus yet) +
 *     CTA to the browse page so user can pick a different combination
 *
 * Accessibility (WCAG 2.2 AA):
 *   - Section is aria-labelled by its h2
 *   - Loading uses role='status' aria-live='polite'
 *   - Empty + error states surface via the same patterns
 *   - CTA is a Link with focus-visible outline
 */

export interface RuleDiffBrowsePanelProps {
  profile: UserProfile;
  currentState: UsStateCode;
}

type PanelState =
  | { kind: "loading" }
  | { kind: "ready"; entries: RuleDiffSeedEntry[]; topEntries: RuleDiffSeedEntry[] }
  | { kind: "not-found" }
  | { kind: "error"; message: string };

const SEVERITY_RANK: Record<RuleDiffSeverity, number> = {
  critical: 0,
  warning: 1,
  match: 2,
  neutral: 3,
};

/**
 * Pick the top N entries to surface. Sort by severity (critical first),
 * tie-broken by canonical display order.
 */
function pickTopEntries(
  entries: RuleDiffSeedEntry[],
  count: number,
): RuleDiffSeedEntry[] {
  const ordered = [...entries].sort((a, b) => {
    const sa = SEVERITY_RANK[parseSeverity(a.reviewer_notes)];
    const sb = SEVERITY_RANK[parseSeverity(b.reviewer_notes)];
    if (sa !== sb) return sa - sb;
    return (
      ATTR_DISPLAY_ORDER.indexOf(a.attr_name) -
      ATTR_DISPLAY_ORDER.indexOf(b.attr_name)
    );
  });
  return ordered.slice(0, count);
}

export default function RuleDiffBrowsePanel({
  profile,
  currentState,
}: RuleDiffBrowsePanelProps) {
  const { t } = useTranslation();
  const [panelState, setPanelState] = useState<PanelState>({ kind: "loading" });

  // Resolve country + state codes for the seed lookup. Country comes
  // from profile.home_country (ISO 3166 uppercase) → corpus code via
  // isoToBrowseCode (handles GB↔uk). State comes from currentState
  // (UsStateCode uppercase) → lowercased + catalog-verified.
  const country = isoToBrowseCode(profile.home_country) ?? null;
  const stateCode = getBrowseState(currentState)
    ? currentState.toLowerCase()
    : null;

  useEffect(() => {
    let cancelled = false;
    if (!country || !stateCode) {
      setPanelState({ kind: "not-found" });
      return;
    }
    setPanelState({ kind: "loading" });
    void (async () => {
      try {
        const entries = await getSeedRuleDiff(country, stateCode);
        if (cancelled) return;
        const topEntries = pickTopEntries(entries, 3);
        setPanelState({ kind: "ready", entries, topEntries });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setPanelState({ kind: "not-found" });
          return;
        }
        const message =
          err instanceof Error
            ? err.message
            : t("dashboard.ruleDiffBrowse.errorGeneric");
        setPanelState({ kind: "error", message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [country, stateCode, t]);

  return (
    <section
      data-testid="dashboard-rule-diff-browse-panel"
      aria-labelledby="dashboard-rule-diff-browse-heading"
      className="rounded-lg border border-brand-primary/10 bg-white p-md shadow-sm sm:p-lg"
    >
      <header className="flex flex-col gap-xs sm:flex-row sm:items-baseline sm:justify-between">
        <h2
          id="dashboard-rule-diff-browse-heading"
          className="font-display text-xl font-bold text-brand-primary"
        >
          {t("dashboard.ruleDiffBrowse.title")}
        </h2>
        <Link
          to={`/rule-diff/browse`}
          data-testid="dashboard-rule-diff-browse-cta"
          className="text-sm font-medium text-brand-primary underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("dashboard.ruleDiffBrowse.seeAllCta")}
        </Link>
      </header>
      <p className="mt-sm text-sm text-neutral-800">
        {t("dashboard.ruleDiffBrowse.subtitle")}
      </p>

      <div className="mt-md">
        {panelState.kind === "loading" && (
          <p
            role="status"
            aria-live="polite"
            className="text-sm text-neutral-600"
          >
            {t("dashboard.ruleDiffBrowse.loading")}
          </p>
        )}
        {panelState.kind === "not-found" && (
          <div
            role="status"
            className="rounded-md border border-amber-300 bg-amber-50 p-sm text-sm text-amber-900"
          >
            <p className="font-semibold">
              {t("dashboard.ruleDiffBrowse.notFoundTitle")}
            </p>
            <p className="mt-xs">
              {t("dashboard.ruleDiffBrowse.notFoundBody")}
            </p>
            <Link
              to="/rule-diff/browse"
              data-testid="dashboard-rule-diff-browse-empty-cta"
              className="mt-sm inline-block text-sm font-medium text-brand-primary underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              {t("dashboard.ruleDiffBrowse.notFoundCta")}
            </Link>
          </div>
        )}
        {panelState.kind === "error" && (
          <div
            role="alert"
            className="rounded-md border border-rose-300 bg-rose-50 p-sm text-sm text-rose-900"
          >
            <p className="font-semibold">
              {t("dashboard.ruleDiffBrowse.errorTitle")}
            </p>
            <p className="mt-xs">{panelState.message}</p>
          </div>
        )}
        {panelState.kind === "ready" && (
          <ul
            className="flex flex-col gap-sm"
            aria-label={t("dashboard.ruleDiffBrowse.topAriaLabel")}
          >
            {panelState.topEntries.map((entry) => (
              <li key={entry.attr_name}>
                <RuleDiffAttributeCard entry={entry} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
